<template>
  <div class="root">
    <div class="background-image" :style="backgroundImage"/>
    <div :style="homeStyles" class="home">
      <div
          @mouseup="adminMenuRelease"
          @touchend="adminMenuRelease"
          class="content">
        <h1 class="font-size-big">{{ roomName }}</h1>
        <div v-if="onlineStatus">
          <template v-if="hybridOfficeEnabled">
            <hybrid-office-current-meeting class="current-actions" v-if="currentBooking"/>
            <hybrid-office-grab-meeting class="current-actions" v-else/>
          </template>
          <template v-else>
            <current-meeting class="current-actions" v-if="currentBooking"/>
            <grab-meeting class="current-actions" v-else/>

            <next-meeting class="next"/>
          </template>
        </div>

        <time
            :datetime="formattedTime"
            @mousedown="adminMenuPress"
            @touchstart="adminMenuPress"
            class="clock font-size-medium">
          {{ formattedTime }}
        </time>
        <v-list class="adminlist" color="transparent" v-if="adminActivated">
          <v-list-item v-if="updateAvailable">
            <v-btn @click="updateApp">Update</v-btn>
          </v-list-item>
          <v-list-item>
            <v-btn class="d-inline-block" @click="fullLogout" color="secondary">Logout</v-btn>
            <span class="px-2 d-inline-block caption grey--text text--lighten-3 text-center">{{ versionName }}</span>
          </v-list-item>
        </v-list>
      </div>
      <go-to-setup/>
    </div>
    <notify-toast/>
  </div>
</template>

<script>
// @ is an alias to /src

import CurrentMeeting from '@/components/CurrentMeeting';
import GrabMeeting from '@/components/GrabMeeting';
import NextMeeting from '@/components/NextMeeting';
import GoToSetup from '@/components/GoToSetup';
import HybridOfficeCurrentMeeting from '@/hybrid-office/HybridOfficeCurrentMeeting';
import HybridOfficeGrabMeeting from '@/hybrid-office/HybridOfficeGrabMeeting';
import store from '@/store';
import {mapActions, mapGetters, mapState} from 'vuex';

export default {
  name: 'Home',
  components: {
    HybridOfficeGrabMeeting,
    HybridOfficeCurrentMeeting,
    NextMeeting,
    CurrentMeeting,
    GrabMeeting,
    GoToSetup
  },
  data() {
    return {
      adminPressTimer: 0,
      adminActivated: false
    };
  },
  computed: {
    ...mapGetters('config', ['style', 'background', 'timeFormat']),
    ...mapGetters('room', ['roomState', 'roomName']),
    ...mapGetters('time', ['now']),
    ...mapGetters('versions', ['versionName']),
    ...mapGetters('hybridOffice', {
      hybridOfficeEnabled: 'enabled'
    }),
    ...mapState('serviceWorker', ['updated']),
    ...mapState('offline', ['onlineStatus']),
    currentBooking() {
      if (this.hybridOfficeEnabled) {
        return this.$store.getters['hybridOffice/bookings/currentBooking'];
      }
      return this.$store.getters['bookings/currentBooking'];
    },
    updateAvailable() {
      return this.updated;
    },
    backgroundImage() {
      return {
        backgroundImage: `url(${this.background})`
      };
    },
    homeStyles() {
      const borderName = this.roomState.toLowerCase();
      return {
        borderColor: `var(--theme-${borderName})`
      };
    },
    formattedTime() {
      return this.formatTime(this.now);
    }
  },
  methods: {
    ...mapActions('serviceWorker', ['reload', 'update']),
    async updateApp() {
      // upgrade the service worker and refresh the page
      await this.reload();
      window.location.reload();
    },
    adminMenuPress() {
      if (this.adminActivated) {
        this.adminActivated = false;
      } else {
        this.adminPressTimer = setTimeout(() => {
          this.adminActivated = true;
        }, 5000);
      }
    },
    adminMenuRelease() {
      clearTimeout(this.adminPressTimer);
    },
    fullLogout() {
      store.dispatch('auth/signOut')
          .catch(err => this.$logger.warn('during logout'))
          .then(() => this.reload());
    }
  }
};
</script>

<style>
body,
.wrapper,
.v-application--wrap {
  width: 1280px;
  height: 800px;
  overflow: hidden;
  min-height: unset;
}

body {
  --theme-primary: #374355;
  --theme-secondary: #0081D5;

  --theme-maintenance: #92278F;
  --theme-available: #03BD5B;
  --theme-booked: #FF9947;
  --theme-occupied: #D13337;

  --grid-size: 80px;
  --border-size: 15px;
  --content-gutter: calc(var(--grid-size) - var(--border-size));
  --button-height: 50px;
  --button-width: 180px;

  font-family: "Arial", sans-serif;
}

* {
  color: var(--theme-primary);
}

html, body {
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.v-application {
  font-family: inherit;
}

.root {
  width: 100%;
  height: 100%;
}

.home {
  width: 100%;
  height: 100%;
  border: var(--border-size) solid var(--theme-maintenance);
  position: relative;
}

.background-image {
  width: 1280px;
  height: 800px;
  position: absolute;
  background: #FFF no-repeat 100% center;
}

.content {
  margin: var(--content-gutter);
  height: calc(100% - (2 * var(--content-gutter)));
  width: calc(100% - (2 * var(--content-gutter)));
  position: relative;
}

.current-actions {
  position: absolute;
  top: calc(2 * var(--grid-size));
  left: 0;
  z-index: 10;
}

.next {
  position: absolute;
  top: calc((3 * var(--grid-size)) + 1.4em);
  right: 0;
}

.line-height-single {
  line-height: 1em;
}

.font-size-big {
  font-size: 64px;
}

.font-size-medium {
  font-size: 48px;
}

.font-size-small {
  font-size: 30px;
}

.text-color-primary {
  color: var(--theme-primary);
}

.text-color-secondary {
  color: var(--theme-secondary);
}

.clock {
  display: block;
  position: absolute;
  bottom: 0;
  left: 0;
  line-height: 0.65em;
  z-index: 1;
}

.adminlist {
  display: grid;
  position: absolute;
  bottom: 0;
  left: calc(2 * var(--grid-size));
  padding-bottom: 0;
  margin-bottom: -7px;
}

.version {
  display: inline-block;
  font-size: 10px;
  padding: 0 0 0 10px;
}
</style>
